import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  eventangaben as ladeAngaben,
  eventangabenSetzen,
  eventleistungenSetzen,
  type Eventangaben as Angaben,
} from '../api/produkte';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Abschnitt } from '../komponenten/Akte';

/**
 * Die Eventangaben am Produkt (PRD-33).
 *
 * ============================================================================
 * VIER DINGE, DREI HERKÜNFTE
 * ============================================================================
 *
 * „Eventprodukte tragen zusätzlich Ort, Rahmen, benötigte Räume und externe
 * Leistungen."
 *
 * Ort und Rahmen gehören dem Event. Die zugekauften Leistungen auch. Die
 * RÄUME NICHT: Sie stehen im Ressourcenbedarf, wo auch Fahrmodule und Trainer
 * stehen, und werden dort gepflegt. Hier erscheinen sie nur — weil ein
 * Anwender, der den Ort einträgt, im selben Atemzug wissen will, welche Räume
 * das Event braucht.
 *
 * Ein zweiter Pflegeweg wäre eine zweite Wahrheit über dieselbe Sache, und
 * die Tagesplanung läse die falsche.
 *
 * ============================================================================
 * ZWEI SPEICHERBEGRIFFE, GETRENNT GEHALTEN
 * ============================================================================
 *
 * Ort und Rahmen sichern wie die Stammdaten: beim Verlassen des Feldes. Die
 * Leistungsliste ist ein Stand, den man als Ganzes schickt — sie hat einen
 * eigenen Knopf.
 *
 * Beides in einen „Speichern"-Knopf zu legen wäre die Falle, die die
 * Artikelakte gerade vermeidet: ein Knopf, der nur die halbe Maske meint.
 */

type Leistungsentwurf = {
  readonly bezeichnung: string;
  readonly anbieter: string;
  readonly bemerkung: string;
};

function ausAngaben(a: Angaben): Leistungsentwurf[] {
  return a.leistungen.map((l) => ({
    bezeichnung: l.bezeichnung,
    anbieter: l.anbieter ?? '',
    bemerkung: l.bemerkung ?? '',
  }));
}

export function Eventangaben({
  artikel,
  darfAendern,
  beiSchliessen,
}: {
  readonly artikel: string;
  readonly darfAendern: boolean;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [angaben, setzeAngaben] = useState<Angaben | null>(null);
  const [ort, setzeOrt] = useState('');
  const [rahmen, setzeRahmen] = useState('');
  const [bemerkung, setzeBemerkung] = useState('');
  const [entwurf, setzeEntwurf] = useState<Leistungsentwurf[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      const a = await ladeAngaben(artikel);
      setzeAngaben(a);
      setzeOrt(a.ort ?? '');
      setzeRahmen(a.rahmen ?? '');
      setzeBemerkung(a.bemerkung ?? '');
      setzeEntwurf(ausAngaben(a));
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [artikel]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /** Ort, Rahmen und Bemerkung sichern — beim Verlassen des Feldes. */
  async function sichereKopf(): Promise<void> {
    if (!darfAendern || angaben === null) return;
    const unveraendert =
      ort.trim() === (angaben.ort ?? '')
      && rahmen.trim() === (angaben.rahmen ?? '')
      && bemerkung.trim() === (angaben.bemerkung ?? '');
    if (unveraendert) return;

    try {
      await eventangabenSetzen(artikel, {
        ort: ort.trim() === '' ? null : ort.trim(),
        rahmen: rahmen.trim() === '' ? null : rahmen.trim(),
        bemerkung: bemerkung.trim() === '' ? null : bemerkung.trim(),
      });
      setzeMeldung('Gespeichert.');
      await holen();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }

  async function sichereLeistungen(): Promise<void> {
    try {
      await eventleistungenSetzen(
        artikel,
        entwurf
          /* Leere Zeilen sind Tippansätze und keine Leistungen. */
          .filter((z) => z.bezeichnung.trim() !== '')
          .map((z) => ({
            bezeichnung: z.bezeichnung.trim(),
            anbieter: z.anbieter.trim() === '' ? null : z.anbieter.trim(),
            bemerkung: z.bemerkung.trim() === '' ? null : z.bemerkung.trim(),
          })),
      );
      setzeMeldung('Die zugekauften Leistungen sind gespeichert.');
      await holen();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }

  const leistungenGeaendert =
    angaben !== null && JSON.stringify(entwurf) !== JSON.stringify(ausAngaben(angaben));

  if (laedt) return <p className="hinweiszeile" role="status">Wird geladen …</p>;

  /*
   * ÄA-41: Die Eventangaben stehen in der Artikelakte an der Stelle der
   * Reiter — Kopf und Seitenleiste des Artikels bleiben. Deshalb eine
   * Überschrift der zweiten Stufe: Die erste ist der Artikel.
   */
  return (
    <div className="tafel__abschnitte">
      <div className="karte__titel">
        <h2>Eventangaben</h2>
        {angaben !== null && <small>{angaben.bezeichnung}</small>}
      </div>
      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={beiSchliessen}>
          Zurück zum Produkt
        </button>
      </div>

      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      <Abschnitt titel="Ort und Rahmen">

        <div className="feld">
          <label htmlFor="event-ort">Ort</label>
          <input
            id="event-ort"
            type="text"
            className="feld__eingabe"
            value={ort}
            disabled={!darfAendern}
            onChange={(e) => {
              setzeOrt(e.target.value);
            }}
            onBlur={() => void sichereKopf()}
          />
          <p className="feld__hilfe">
            Wo das Event stattfindet — auch außerhalb des Zentrums, etwa beim Kunden oder in
            einem Hotel. Findet es auf dem Gelände statt, steht der Raum weiter unten.
          </p>
        </div>

        <div className="feld">
          <label htmlFor="event-rahmen">Rahmen</label>
          <input
            id="event-rahmen"
            type="text"
            className="feld__eingabe"
            value={rahmen}
            disabled={!darfAendern}
            onChange={(e) => {
              setzeRahmen(e.target.value);
            }}
            onBlur={() => void sichereKopf()}
          />
          <p className="feld__hilfe">
            In welchem Rahmen das Event steht — Firmenfeier, Incentive, Produktvorstellung.
          </p>
        </div>

        <div className="feld">
          <label htmlFor="event-bemerkung">Bemerkung</label>
          <textarea
            id="event-bemerkung"
            className="feld__eingabe"
            value={bemerkung}
            rows={3}
            disabled={!darfAendern}
            onChange={(e) => {
              setzeBemerkung(e.target.value);
            }}
            onBlur={() => void sichereKopf()}
          />
        </div>
      </Abschnitt>

      {/*
        DIE RÄUME WERDEN GEZEIGT, NICHT GEPFLEGT (PRD-29, PRD-33).

        Sie stehen im Ressourcenbedarf, wo auch Fahrmodule und Trainer stehen.
        Ein zweites Eingabefeld hier ergäbe zwei Wahrheiten über dieselbe
        Sache — und die Tagesplanung läse die falsche.
      */}
      <Abschnitt titel="Benötigte Räume">
        {angaben === null || angaben.raeume.length === 0 ? (
          <p className="hilfe">
            Für dieses Event ist kein Raum vorgesehen. Räume werden als Ressourcenbedarf
            gepflegt — im Reiter „Programm".
          </p>
        ) : (
          <ul className="merkmalsliste">
            {angaben.raeume.map((r) => (
              <li key={r.bezeichnung}>
                {r.bezeichnung}
                {r.anzahl > 1 ? ` (${String(r.anzahl)}×)` : ''}
              </li>
            ))}
          </ul>
        )}
        <p className="feld__hilfe">
          Aus dem Ressourcenbedarf. Ein Raum ist eine Ressource wie ein Fahrmodul — geplant
          wird er im selben Tagesplan.
        </p>
      </Abschnitt>

      <Abschnitt titel="Externe Leistungen" zusatz={entwurf.length}>

        {entwurf.length === 0 ? (
          <p className="hilfe">Für dieses Event ist nichts zugekauft.</p>
        ) : (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <thead>
                <tr>
                  <th scope="col">Leistung</th>
                  <th scope="col">Anbieter</th>
                  <th scope="col">Bemerkung</th>
                  {darfAendern && <th scope="col">Entfernen</th>}
                </tr>
              </thead>
              <tbody>
                {entwurf.map((z, i) => (
                  /* Die Position IST die Reihenfolge — ein Index als Schlüssel ist
                     hier genau das Richtige: Wird eine Zeile entfernt, rücken die
                     folgenden auf, und das ist das gewünschte Verhalten. */
                  <tr key={i}>
                    <td>
                      <input
                        type="text"
                        className="feld__eingabe"
                        aria-label={`Leistung ${String(i + 1)}`}
                        value={z.bezeichnung}
                        disabled={!darfAendern}
                        onChange={(e) => {
                          const wert = e.target.value;
                          setzeEntwurf((alt) =>
                            alt.map((a, j) => (j === i ? { ...a, bezeichnung: wert } : a)),
                          );
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="feld__eingabe"
                        aria-label={`Anbieter ${String(i + 1)}`}
                        value={z.anbieter}
                        disabled={!darfAendern}
                        onChange={(e) => {
                          const wert = e.target.value;
                          setzeEntwurf((alt) =>
                            alt.map((a, j) => (j === i ? { ...a, anbieter: wert } : a)),
                          );
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="feld__eingabe"
                        aria-label={`Bemerkung ${String(i + 1)}`}
                        value={z.bemerkung}
                        disabled={!darfAendern}
                        onChange={(e) => {
                          const wert = e.target.value;
                          setzeEntwurf((alt) =>
                            alt.map((a, j) => (j === i ? { ...a, bemerkung: wert } : a)),
                          );
                        }}
                      />
                    </td>
                    {darfAendern && (
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--eingriff knopf--klein"
                          onClick={() => {
                            setzeEntwurf((alt) => alt.filter((_, j) => j !== i));
                          }}
                        >
                          Entfernen
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {darfAendern && (
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeEntwurf((alt) => [...alt, { bezeichnung: '', anbieter: '', bemerkung: '' }]);
              }}
            >
              Leistung hinzufügen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={!leistungenGeaendert}
              onClick={() => void sichereLeistungen()}
            >
              Leistungen speichern
            </button>
          </div>
        )}

        <p className="feld__hilfe">
          Was für dieses Event zugekauft wird und von wem. Ohne Preis — was der Zukauf kostet,
          steht am Einkauf und nicht am Produkt.
        </p>
      </Abschnitt>
    </div>
  );
}
